import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import GuardianIcon from "@/components/common/GuardianIcon"
import GuardiansHeaderBar from "@/components/GuardiansHeaderBar"
import { getRadarStatus } from "@/api/gameApi"
import { getDailyTournamentCurrent } from "@/api/dailyVirtualTournamentApi"
import { getVirtualTournamentCurrent } from "@/api/virtualTournamentApi"
import {
  DECK_ENERGY_COST,
  RADAR_ENERGY_COST,
} from "@/constants/game.constants"
import { DECK_BATTLE_ENABLED, USE_MOCK_BATTLE } from "@/constants/guardians"
import { assetUrl } from "@/constants/assetUrl"
import { useAuth } from "@/context/AuthContext"
import AlmanacHomeProgressCard from "@/features/homeMotivation/AlmanacHomeProgressCard"
import GuardianLadderCard from "@/features/homeMotivation/GuardianLadderCard"
import HomeQuestsTile from "@/features/homeMotivation/HomeQuestsTile"
import SeasonChampionBanner from "@/features/homeMotivation/SeasonChampionBanner"
import { useHomeMotivation } from "@/features/homeMotivation/useHomeMotivation"
import { resolveGuardiansCityLabel, getCachedGuardiansCities } from "@/features/passport/guardiansCity"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { formatVirtualTournamentCountdown } from "@/features/virtualTournament/virtualTournamentInfo"
import { useVirtualTournamentCountdownMs } from "@/features/virtualTournament/useVirtualTournamentCountdown"
import { ensureAffiliationForPvp } from "@/utils/affiliationGuard"
import { hasSelectedLeague } from "@/utils/onboardingPassport"
import { RadarStatus } from "@/types/battle"
import { VirtualTournamentCurrent } from "@/types/virtualTournament"
import { DailyTournamentCurrent } from "@/types/dailyVirtualTournament"

export default function GuardiansHomePage() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const { passport, loading, syncGameState } = useGuardiansPassport()
  const [refreshing, setRefreshing] = useState(false)
  const [refreshGeneration, setRefreshGeneration] = useState(0)
  const {
    status: motivation,
    loading: motivationLoading,
    setStatus: setMotivation,
  } = useHomeMotivation(refreshGeneration)
  const [radarStatus, setRadarStatus] = useState<RadarStatus | null>(null)
  const [virtualTournament, setVirtualTournament] =
    useState<VirtualTournamentCurrent | null>(null)
  const [dailyTournament, setDailyTournament] =
    useState<DailyTournamentCurrent | null>(null)

  const loadRadarStatus = useCallback(async () => {
    if (USE_MOCK_BATTLE || !passport) {
      setRadarStatus(null)
      return
    }
    try {
      const status = await getRadarStatus()
      setRadarStatus(status)
    } catch {
      setRadarStatus(null)
    }
  }, [passport])

  const loadVirtualTournament = useCallback(async () => {
    if (USE_MOCK_BATTLE || !passport) {
      setVirtualTournament(null)
      return
    }
    try {
      setVirtualTournament(await getVirtualTournamentCurrent())
    } catch {
      setVirtualTournament(null)
    }
  }, [passport])

  const loadDailyTournament = useCallback(async () => {
    if (USE_MOCK_BATTLE || !passport) {
      setDailyTournament(null)
      return
    }
    try {
      setDailyTournament(await getDailyTournamentCurrent())
    } catch {
      setDailyTournament(null)
    }
  }, [passport])

  useEffect(() => {
    void syncGameState()
    void loadRadarStatus()
    void loadVirtualTournament()
    void loadDailyTournament()
  }, [syncGameState, loadRadarStatus, loadVirtualTournament, loadDailyTournament])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    try {
      await syncGameState()
      await Promise.all([
        loadRadarStatus(),
        loadVirtualTournament(),
        loadDailyTournament(),
      ])
      setRefreshGeneration((g) => g + 1)
    } finally {
      setRefreshing(false)
    }
  }, [loadRadarStatus, loadVirtualTournament, loadDailyTournament, syncGameState])

  const startDeckDuel = () => {
    if (!passport) return
    if (!hasSelectedLeague(passport)) {
      window.alert("Сначала выберите лигу в Академии")
      return
    }
    if (!ensureAffiliationForPvp(passport, navigate)) return
    if (passport.energy < DECK_ENERGY_COST) {
      window.alert(
        `Для дуэли колодой нужно ${DECK_ENERGY_COST} энергии (${passport.energy}/${passport.maxEnergy})`
      )
      return
    }
    navigate("/play/deck-builder?mode=radar")
  }

  const startRadar = () => {
    if (!passport) return
    if (!hasSelectedLeague(passport)) {
      window.alert("Сначала выберите лигу в Академии")
      return
    }
    if (!ensureAffiliationForPvp(passport, navigate)) return
    if (passport.energy < RADAR_ENERGY_COST) {
      window.alert(
        `Для боя нужно ${RADAR_ENERGY_COST} энергии (${passport.energy}/${passport.maxEnergy})`
      )
      return
    }
    navigate("/play/battle-lobby")
  }

  const openArena = () => {
    if (!passport) return
    if (!hasSelectedLeague(passport)) {
      window.alert("Сначала выберите лигу в Академии")
      return
    }
    if (!ensureAffiliationForPvp(passport, navigate)) return
    navigate("/play/arena")
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-guardians-accent border-t-transparent" />
      </div>
    )
  }

  if (!passport) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 px-6">
        <p className="text-sm text-guardians-muted">Игровой профиль не создан</p>
        <Link
          to="/play/onboarding"
          className="rounded-xl bg-guardians-accent px-6 py-3 text-sm font-semibold text-guardians-bg"
        >
          Начать игру
        </Link>
      </div>
    )
  }

  const cityLabel = resolveGuardiansCityLabel(
    passport,
    null,
    profile as { city?: { title?: string } | string | null; city_id?: number },
    getCachedGuardiansCities()
  )

  return (
    <div className="px-5 py-5 pb-8">
      <div className="mb-4 flex flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-extrabold text-white">{passport.nickname}</h1>
          <p className="text-sm font-semibold text-white/45">{cityLabel}</p>
        </div>
        <div className="flex shrink-0 flex-row items-center gap-2">
          <Link
            to="/play/help"
            className="rounded-xl border border-white/10 bg-white/5 p-2"
            aria-label="Помощь"
          >
            <GuardianIcon name="question" size={22} />
          </Link>
          <Link
            to="/play/rules"
            className="rounded-xl border border-white/10 bg-white/5 p-2"
            aria-label="Правила"
          >
            <GuardianIcon name="rules" size={22} />
          </Link>
        </div>
      </div>

      <div className="mb-4">
        <GuardiansHeaderBar passport={passport} />
      </div>

      <button
        type="button"
        disabled={refreshing}
        onClick={() => void onRefresh()}
        className="mb-4 w-full rounded-lg border border-white/10 py-1.5 text-xs text-white/50"
      >
        {refreshing ? "Обновление…" : "Потяните или нажмите для обновления"}
      </button>

      <div key={refreshGeneration} className="flex flex-col gap-[18px]">
        <section className="flex flex-col gap-2.5">
          <h2 className="text-[11px] font-extrabold uppercase tracking-wide text-white/40">
            Ты хранитель
          </h2>
          {motivationLoading && !motivation ? (
            <div className="flex justify-center py-4">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-guardians-accent border-t-transparent" />
            </div>
          ) : motivation ? (
            <>
              <GuardianLadderCard
                ladder={motivation.ladder}
                onUpdated={setMotivation}
              />
              <AlmanacHomeProgressCard
                almanac={motivation.almanac}
                onUpdated={setMotivation}
              />
              <HomeQuestsTile refreshKey={refreshGeneration} />
            </>
          ) : (
            <HomeQuestsTile refreshKey={refreshGeneration} />
          )}
        </section>

        <section className="flex flex-col gap-2.5">
          <h2 className="text-[11px] font-extrabold uppercase tracking-wide text-white/40">
            События
          </h2>
          {motivation ? <SeasonChampionBanner season={motivation.season} /> : null}
          <button
            type="button"
            onClick={() => {
              if (!hasSelectedLeague(passport)) {
                window.alert("Сначала выберите лигу в Академии")
                return
              }
              navigate("/play/world-boss")
            }}
            className="flex w-full min-w-[140px] flex-grow basis-[47%] flex-col items-center rounded-2xl border border-amber-500/30 bg-amber-500/[0.08] px-3 py-3"
          >
            <img
              src={assetUrl("maps/megaBoss.png")}
              alt=""
              className="h-10 w-10 object-contain"
            />
            <span className="mt-1 text-sm font-extrabold text-amber-500">
              Мировой босс
            </span>
            <span className="text-[11px] font-semibold text-white/55">
              рядом бесплатно · иначе 5 энергии
            </span>
          </button>
          <div className="flex flex-row flex-wrap gap-2">
            <VirtualTournamentHomeCard
              current={virtualTournament}
              onPress={() => navigate("/play/virtual-tournament")}
            />
            <DailyTournamentHomeCard
              current={dailyTournament}
              onPress={() => navigate("/play/daily-tournament")}
            />
          </div>
        </section>

        <section className="flex flex-col gap-2.5">
          <h2 className="text-[11px] font-extrabold uppercase tracking-wide text-white/40">
            Играть
          </h2>
          <div className="flex flex-row flex-wrap gap-2">
            <HomeTile
              onClick={startRadar}
              icon="radar"
              title="Найти бой"
              titleClass="text-guardians-accent"
              sub={`${RADAR_ENERGY_COST} энергии${
                radarStatus ? ` · в поиске ${radarStatus.queueSize}` : ""
              }`}
              className="border-guardians-accent/25 bg-guardians-accent/[0.08]"
            />
            <HomeTile
              onClick={() => {
                if (!hasSelectedLeague(passport)) {
                  window.alert("Сначала выберите лигу в Академии")
                  return
                }
                navigate("/play/training")
              }}
              icon="trainer"
              title="Тренировка"
              titleClass="text-sky-400"
              sub="без энергии"
              className="border-sky-400/25 bg-sky-400/[0.08]"
            />
            <HomeTile
              onClick={openArena}
              icon="arena"
              title="Арена"
              titleClass="text-red-500"
              sub="3×3 · команда"
              className="border-red-500/25 bg-red-500/[0.08]"
            />
            {DECK_BATTLE_ENABLED ? (
              <HomeTile
                onClick={startDeckDuel}
                icon="cards"
                title="Колода"
                titleClass="text-teal-400"
                sub={`до 3 побед · ${DECK_ENERGY_COST} эн.`}
                className="border-teal-400/25 bg-teal-400/[0.08]"
              />
            ) : null}
          </div>
          <div className="flex flex-row flex-wrap gap-2">
            <HomeTile
              onClick={() => {
                if (!ensureAffiliationForPvp(passport, navigate)) return
                navigate("/play/matchmaking?mode=QR&role=host")
              }}
              icon="oneonone"
              title="1 на 1"
              sub="создать комнату"
              className="border-white/10 bg-white/[0.04]"
            />
            <HomeTile
              onClick={() => {
                if (!ensureAffiliationForPvp(passport, navigate)) return
                navigate("/play/matchmaking?mode=QR&role=guest")
              }}
              icon="room"
              title="Войти"
              sub="по коду"
              className="border-white/10 bg-white/[0.04]"
            />
          </div>
        </section>

        <div className="flex flex-row gap-2">
          <Link
            to="/play/referral"
            className="flex flex-1 flex-col items-center rounded-2xl border border-violet-400/25 bg-violet-400/[0.08] py-3"
          >
            <GuardianIcon name="friends" size={28} />
            <span className="mt-1 text-sm font-extrabold text-violet-300">Друзья</span>
          </Link>
          <Link
            to="/play/leaderboard"
            className="flex flex-1 flex-col items-center rounded-2xl border border-guardians-accent/25 bg-guardians-accent/[0.08] py-3"
          >
            <GuardianIcon name="rating" size={28} />
            <span className="mt-1 text-sm font-extrabold text-guardians-accent">
              Рейтинг
            </span>
          </Link>
          <Link
            to="/play/shop"
            className="flex flex-1 flex-col items-center rounded-2xl border border-orange-400/25 bg-orange-400/[0.08] py-3"
          >
            <GuardianIcon name="shop" size={28} />
            <span className="mt-1 text-sm font-extrabold text-orange-300">Магазин</span>
          </Link>
        </div>
      </div>
    </div>
  )
}

function HomeTile({
  onClick,
  icon,
  title,
  titleClass,
  sub,
  className,
}: {
  onClick: () => void
  icon: Parameters<typeof GuardianIcon>[0]["name"]
  title: string
  titleClass?: string
  sub: string
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-w-[140px] flex-grow basis-[47%] flex-col items-center rounded-2xl border px-3 py-3 ${className ?? ""}`}
    >
      <GuardianIcon name={icon} size={28} />
      <span className={`mt-1 text-sm font-extrabold ${titleClass ?? "text-white"}`}>
        {title}
      </span>
      <span className="text-center text-[11px] font-semibold text-white/55">{sub}</span>
    </button>
  )
}

function VirtualTournamentHomeCard({
  current,
  onPress,
}: {
  current: VirtualTournamentCurrent | null
  onPress: () => void
}) {
  const remainingMs = useVirtualTournamentCountdownMs(
    current?.nextStartAt ?? "",
    current?.msUntilStart ?? 0
  )

  return (
    <button
      type="button"
      onClick={onPress}
      className="flex min-w-[140px] flex-grow basis-[47%] flex-col items-center rounded-2xl border border-guardians-accent/25 bg-guardians-accent/[0.08] px-3 py-3"
    >
      <GuardianIcon name="kubok" size={26} />
      <span className="mt-1 text-sm font-extrabold text-guardians-accent">
        Турнир недели
      </span>
      <span className="text-[11px] font-semibold text-white/55">
        {current
          ? formatVirtualTournamentCountdown(remainingMs)
          : "6 рангов · автобои"}
      </span>
    </button>
  )
}

function DailyTournamentHomeCard({
  current,
  onPress,
}: {
  current: DailyTournamentCurrent | null
  onPress: () => void
}) {
  const remainingMs = useVirtualTournamentCountdownMs(
    current?.nextStartAt ?? "",
    current?.msUntilStart ?? 0
  )

  return (
    <button
      type="button"
      onClick={onPress}
      className="flex min-w-[140px] flex-grow basis-[47%] flex-col items-center rounded-2xl border border-violet-400/25 bg-violet-400/[0.08] px-3 py-3"
    >
      <GuardianIcon name="daily" size={26} />
      <span className="mt-1 text-sm font-extrabold text-violet-300">Турнир дня</span>
      <span className="text-[11px] font-semibold text-white/55">
        {current
          ? formatVirtualTournamentCountdown(remainingMs)
          : "каждый день"}
      </span>
    </button>
  )
}
